/**
 * UI-30: Action Orchestration Engine
 *
 * Pure, deterministic engine that converts:
 *   alerts + risks + pressure + state → ordered, executable actions
 *
 * Guarantees:
 *   - Deterministic: same input → same output.
 *   - Max 5 actions, ideal 3.
 *   - Deduplicated: multiple alerts with same root cause → one action.
 *   - Executable: every action is clear and actionable in <2 seconds.
 *   - No side effects. No React. No hooks.
 */

import type {
  ActionEngineInput,
  ActionEngineResult,
  Action,
  SignalCluster,
  RootProblemType,
} from './actionTypes';
import {
  ALERT_TO_ROOT_PROBLEM,
  RISK_TO_ROOT_PROBLEM,
  ROOT_PROBLEM_DOMAIN,
  ROOT_PROBLEM_ACTION,
  derivePriority,
  computeImpactScore,
  deriveContextLine,
  deriveTargetItems,
  deriveConfidence,
} from './actionMappings';

// ─── Constants ───────────────────────────────────────────────────────────────

const MAX_ACTIONS = 5;

// ─── STEP 1: Normalize Signals → Clusters ────────────────────────────────────

/**
 * Groups alerts and risks by root cause.
 * Multiple alerts mapping to the same root problem collapse into one cluster.
 */
function clusterSignals(input: ActionEngineInput): Map<RootProblemType, SignalCluster> {
  const clusters = new Map<RootProblemType, {
    alerts: typeof input.alerts[number][];
    risks: typeof input.risks[number][];
    maxSeverity: 'info' | 'warning' | 'critical';
  }>();

  const severityRank: Record<string, number> = { info: 0, warning: 1, critical: 2 };

  function ensureCluster(rootProblem: RootProblemType) {
    if (!clusters.has(rootProblem)) {
      clusters.set(rootProblem, { alerts: [], risks: [], maxSeverity: 'info' });
    }
    return clusters.get(rootProblem)!;
  }

  // Cluster alerts
  for (const alert of input.alerts) {
    const rootProblem = ALERT_TO_ROOT_PROBLEM[alert.type];
    if (!rootProblem) continue;

    const cluster = ensureCluster(rootProblem);
    cluster.alerts.push(alert);
    if ((severityRank[alert.severity] ?? 0) > (severityRank[cluster.maxSeverity] ?? 0)) {
      cluster.maxSeverity = alert.severity;
    }
  }

  // Cluster risks
  for (const risk of input.risks) {
    const rootProblem = RISK_TO_ROOT_PROBLEM[risk.type];
    if (!rootProblem) continue;

    const cluster = ensureCluster(rootProblem);
    cluster.risks.push(risk);
    if ((severityRank[risk.severity] ?? 0) > (severityRank[cluster.maxSeverity] ?? 0)) {
      cluster.maxSeverity = risk.severity;
    }
  }

  // Convert to readonly SignalCluster
  const result = new Map<RootProblemType, SignalCluster>();
  for (const [rootProblem, data] of clusters) {
    result.set(rootProblem, {
      rootProblem,
      domain: ROOT_PROBLEM_DOMAIN[rootProblem],
      alerts: data.alerts,
      risks: data.risks,
      maxSeverity: data.maxSeverity,
    });
  }

  return result;
}

// ─── STEP 2–3: Derive Root Problems → Map to Actions ─────────────────────────

function clusterToAction(
  cluster: SignalCluster,
  pressure: ActionEngineInput['pressure'],
  liveRequests?: ActionEngineInput['liveRequests'],
): Action {
  const template = ROOT_PROBLEM_ACTION[cluster.rootProblem];
  const priority = derivePriority(cluster);
  const impactScore = computeImpactScore(cluster, pressure);
  const targetItems = deriveTargetItems(cluster, liveRequests);

  return {
    id: `action-${cluster.rootProblem.toLowerCase()}`,
    title: template.title,
    description: template.description,
    contextLine: deriveContextLine(cluster, template.contextTemplate, targetItems),
    confidence: deriveConfidence(impactScore, cluster),
    priority,
    domain: cluster.domain,
    impactScore,
    signalCount: cluster.alerts.length + cluster.risks.length,
    cta: template.cta,
    targetItems,
  };
}

// ─── STEP 4–5: Score, Sort, Trim ─────────────────────────────────────────────

/**
 * Deterministic sort: by impactScore descending, then by rootProblem name (tiebreaker).
 */
function sortActions(actions: Action[]): Action[] {
  return actions.sort((a, b) => {
    if (b.impactScore !== a.impactScore) return b.impactScore - a.impactScore;
    // Stable tiebreaker: alphabetical by id
    return a.id.localeCompare(b.id);
  });
}

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Compute ordered, executable actions from the current operational state.
 *
 * Pure function. No side effects.
 * Same input → same output, always.
 *
 * UI-3.1.5: Situation awareness layer — in SURGE or CRITICAL_OVERLOAD,
 * returns exactly one synthesized action, bypassing the normal pipeline.
 */
export function computeActions(input: ActionEngineInput): ActionEngineResult {
  // No alerts and no risks → no actions
  if (input.alerts.length === 0 && input.risks.length === 0) {
    return { actions: [] };
  }

  // Always run the full pipeline — all distinct problems surfaced regardless of situation level.
  // SURGE/CRITICAL_OVERLOAD context is already communicated via the incident banner and pulse gauge;
  // collapsing to a single action here hides real problems from the supervisor.
  // Step 1: Cluster signals by root cause
  const clusters = clusterSignals(input);

  // Steps 2–3: Map each cluster to an action
  const actions: Action[] = [];
  for (const cluster of clusters.values()) {
    actions.push(clusterToAction(cluster, input.pressure, input.liveRequests));
  }

  // Steps 4–5: Sort by impact and trim to max
  const sorted = sortActions(actions);
  const trimmed = sorted.slice(0, MAX_ACTIONS);

  return { actions: trimmed };
}
